import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAutoSaveOptions {
  /** Pure work function – called without reentrancy guard (hook handles it). */
  flush: () => Promise<void>;
  /** Whether there is anything to save. Debounce timer is cleared when false. */
  isDirty: boolean;
  /** Increments on every change to reset the debounce timer. */
  changeToken: number;
  /** Debounce delay in ms. Default: 5000. */
  delayMs?: number;
}

interface UseAutoSaveResult {
  /** True while flush is running. */
  isSaving: boolean;
  /** Stable ref – readable from async contexts without stale closure. */
  isSavingRef: React.MutableRefObject<boolean>;
  /** Manually trigger save (fire-and-forget, reentrancy safe). */
  triggerSave: () => Promise<void>;
}

/**
 * Wraps an async flush function with:
 * - reentrancy guard (a running flush blocks new calls)
 * - isSaving state for UI feedback
 * - debounced auto-save triggered by changeToken
 */
export function useAutoSave({
  flush,
  isDirty,
  changeToken,
  delayMs = 5000,
}: UseAutoSaveOptions): UseAutoSaveResult {
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const triggerSave = useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await flushRef.current();
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, []);

  const triggerSaveRef = useRef(triggerSave);
  triggerSaveRef.current = triggerSave;

  useEffect(() => {
    if (!isDirty || isSaving) return;
    const timer = setTimeout(() => {
      void triggerSaveRef.current();
    }, delayMs);
    return () => clearTimeout(timer);
  }, [changeToken, isDirty, isSaving, delayMs]);

  return { isSaving, isSavingRef, triggerSave };
}
